import { z } from "zod";

const MONEY_FORMAT = /^\d+(\.\d{1,2})?$/;
const PLANNED_KEYS = [
  "salary_planned",
  "taxes_planned",
  "rent_planned",
  "supplies_planned",
  "travel_planned",
  "fixed_assets_planned",
  "software_planned",
  "gph_planned",
  "msb_services_planned",
  "other_services_planned",
] as const;

const plannedField = z
  .string()
  .optional()
  .or(z.literal(""))
  .refine((val) => !val || MONEY_FORMAT.test(val), { message: "projectForm.errors.plannedFormat" });

const incomeSourceSchema = z.object({
  income_id: z.number(),
  amount: z.string().refine((v) => MONEY_FORMAT.test(v) && parseFloat(v) > 0, {
    message: "projectForm.errors.sourceAmountInvalid",
  }),
});

export function createProjectFormSchema(mode: "create" | "edit") {
  const isCreate = mode === "create";

  return z
    .object({
      name: isCreate
        ? z.string().min(1, "projectForm.errors.nameRequired").max(200, "projectForm.errors.nameMax")
        : z.string().max(200, "projectForm.errors.nameMax").optional().or(z.literal("")),
      description: isCreate
        ? z.string().min(1, "projectForm.errors.descriptionRequired").max(2000, "projectForm.errors.descriptionMax")
        : z.string().max(2000, "projectForm.errors.descriptionMax").optional().or(z.literal("")),
      status: z.enum(["planned", "active", "paused", "closed"]),
      is_internal: z.boolean().optional(),
      sources: z.array(incomeSourceSchema).optional(),
      salary_planned: plannedField,
      taxes_planned: plannedField,
      rent_planned: plannedField,
      supplies_planned: plannedField,
      travel_planned: plannedField,
      fixed_assets_planned: plannedField,
      software_planned: plannedField,
      gph_planned: plannedField,
      msb_services_planned: plannedField,
      other_services_planned: plannedField,
      start_date: isCreate
        ? z.string().min(1, "projectForm.errors.startDateRequired")
        : z.string().optional().or(z.literal("")),
      end_date: isCreate
        ? z.string().min(1, "projectForm.errors.endDateRequired")
        : z.string().optional().or(z.literal("")),
      region_id: isCreate
        ? z
            .number({ error: "projectForm.errors.regionRequired" })
            .nullable()
            .refine((v) => v !== null, { message: "projectForm.errors.regionRequired" })
        : z.number().nullable().optional(),
      category: isCreate
        ? z.string().min(1, "projectForm.errors.categoryRequired")
        : z.string().optional().or(z.literal("")),
      project_picture: z.instanceof(File).nullable().optional(),
      beneficiaries_coverage: isCreate
        ? z.string().min(1, "projectForm.errors.beneficiariesRequired")
        : z.string().optional().or(z.literal("")),
      partners: z
        .array(
          z.object({
            partner_name: z.string().min(1, "projectForm.errors.partnerNameRequired"),
            is_external: z.boolean(),
          }),
        )
        .optional(),
    })
    .superRefine((data, ctx) => {
      if (!isCreate) return;
      if (data.is_internal === true) return;

      const totalSmeta = PLANNED_KEYS.reduce((acc, f) => acc + (parseFloat(data[f] || "0") || 0), 0);
      if (totalSmeta <= 0) {
        ctx.addIssue({
          code: "custom",
          message: "projectForm.errors.plannedRequired",
          path: ["salary_planned"],
        });
      }

      const totalSources = (data.sources ?? []).reduce((acc, s) => acc + (parseFloat(s.amount || "0") || 0), 0);
      if (totalSources <= 0) {
        ctx.addIssue({
          code: "custom",
          message: "projectForm.errors.sourcesRequired",
          path: ["sources"],
        });
      }

      if (totalSmeta > 0 && totalSources > 0 && Math.abs(totalSmeta - totalSources) > 0.01) {
        ctx.addIssue({
          code: "custom",
          message: "projectForm.errors.sourcesSmetaMismatch",
          path: ["sources"],
        });
      }
    });
}

export const projectFormSchema = createProjectFormSchema("create");

export type ProjectFormValues = z.infer<typeof projectFormSchema>;
